/**
 * Plan entitlements catalog and resolver.
 * Vue-free. Keep functions/entitlements.js and the guest-app copy in lockstep.
 */

const SUBSCRIPTION_GRACE_MS = 3 * 24 * 60 * 60 * 1000;

const PRO_STRIPE_PRICE_IDS = [
  "price_1SXkSi03NFhO4KkKPfiFvjxt",
  "price_1SXkkn03NFhO4KkKjG1MOKZE",
  "price_1U1Qqv03NFhO4KkKVgw52JHe",
];

const FEATURE_IDS = [
  "guestAsk",
  "listingImport",
  "whiteLabel",
  "customUrls",
  "proPropertyFields",
];

const FEATURE_LABELS = {
  guestAsk: "Guest Ask AI",
  listingImport: "Import from listing",
  whiteLabel: "White label / branding",
  customUrls: "Custom URLs",
  proPropertyFields: "Pro property fields",
};

const NONE = {
  guestAsk: false,
  listingImport: false,
  whiteLabel: false,
  customUrls: false,
  proPropertyFields: false,
};

const GUEST_ASK_FEATURES = {
  ...NONE,
  guestAsk: true,
  listingImport: true,
};

const PRO_FEATURES = {
  guestAsk: true,
  listingImport: true,
  whiteLabel: true,
  customUrls: true,
  proPropertyFields: true,
};

const PLAN_CATALOG = {
  trial: {
    family: "free",
    label: "Trial",
    limits: { properties: 3 },
    features: { ...NONE },
  },
  Monthly: {
    family: "standard",
    label: "Monthly",
    limits: { properties: 5 },
    features: { ...NONE },
  },
  Annual: {
    family: "standard",
    label: "Annual",
    limits: { properties: 5 },
    features: { ...NONE },
  },
  Annual10: {
    family: "standard",
    label: "Annual10",
    limits: { properties: 10 },
    features: { ...NONE },
  },
  GuestAsk: {
    family: "guestAsk",
    label: "Guest Ask",
    limits: { properties: 5 },
    features: { ...GUEST_ASK_FEATURES },
  },
  "PRO10-90": {
    family: "pro",
    label: "PRO10-90",
    limits: { properties: 10 },
    features: { ...PRO_FEATURES },
  },
  Pro: {
    family: "pro",
    label: "Pro",
    limits: { properties: 100 },
    features: { ...PRO_FEATURES },
  },
};

const PLAN_ALIASES = {
  sub_01m: "Monthly",
  sub_12m: "Annual",
  sub_100_12m: "Pro",
  "Pro-10": "PRO10-90",
  PRO10: "PRO10-90",
};

const GRANT_PLAN_KEYS = ["GuestAsk", "PRO10-90", "Pro"];

const GRANT_DURATION_OPTIONS = [
  { label: "30 days", days: 30 },
  { label: "90 days", days: 90 },
  { label: "6 months", days: 180 },
  { label: "1 year", days: 365 },
];

function normalizeTypeKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[_\s-]+/g, "");
}

function getAccount(org) {
  return (org && org.account) || {};
}

function getAccountExpiresMs(account) {
  if (typeof account.expiresAsNumber === "number") {
    return account.expiresAsNumber;
  }
  const expires = account.expires;
  if (expires === undefined || expires === null) {
    return null;
  }
  if (typeof expires === "number") {
    return expires;
  }
  if (typeof expires.toDate === "function") {
    return expires.toDate().getTime();
  }
  const parsed = Date.parse(expires);
  return Number.isNaN(parsed) ? null : parsed;
}

function resolveCatalogPlanKey(value) {
  if (value && PLAN_CATALOG[value]) {
    return value;
  }
  if (value && PLAN_ALIASES[value]) {
    return PLAN_ALIASES[value];
  }
  return null;
}

function getEntitlementGrant(org) {
  const grant = getAccount(org).entitlementGrant;
  if (!grant || typeof grant !== "object") {
    return null;
  }
  return grant;
}

function getGrantExpiresMs(grant) {
  if (!grant) {
    return null;
  }
  if (typeof grant.expiresAsNumber === "number") {
    return grant.expiresAsNumber;
  }
  if (grant.expires === undefined || grant.expires === null) {
    return null;
  }
  if (typeof grant.expires === "number") {
    return grant.expires;
  }
  if (typeof grant.expires.toDate === "function") {
    return grant.expires.toDate().getTime();
  }
  const parsed = Date.parse(grant.expires);
  return Number.isNaN(parsed) ? null : parsed;
}

function getActiveGrantPlanKey(org, nowMs) {
  const now = nowMs == null ? Date.now() : nowMs;
  const grant = getEntitlementGrant(org);
  if (!grant) {
    return null;
  }
  const planKey = resolveCatalogPlanKey(grant.planKey);
  if (!planKey || GRANT_PLAN_KEYS.indexOf(planKey) === -1) {
    return null;
  }
  const expiresMs = getGrantExpiresMs(grant);
  if (expiresMs === null) {
    return planKey;
  }
  if (expiresMs + SUBSCRIPTION_GRACE_MS > now) {
    return planKey;
  }
  return null;
}

function isEntitlementGrantActive(org, nowMs) {
  return !!getActiveGrantPlanKey(org, nowMs);
}

function buildEntitlementGrant(options) {
  const opts = options || {};
  const now = opts.nowMs == null ? Date.now() : opts.nowMs;
  const planKey = resolveCatalogPlanKey(opts.planKey);
  if (!planKey || GRANT_PLAN_KEYS.indexOf(planKey) === -1) {
    return null;
  }
  let expiresMs = opts.expiresAtMs;
  if (expiresMs == null && opts.durationDays) {
    const days = Number(opts.durationDays);
    if (Number.isFinite(days) && days > 0) {
      expiresMs = now + days * 24 * 60 * 60 * 1000;
    }
  }
  const grant = {
    planKey: planKey,
    grantedAt: new Date(now).toISOString(),
    grantedBy: opts.grantedBy || null,
  };
  if (expiresMs != null && Number.isFinite(expiresMs)) {
    grant.expires = new Date(expiresMs).toISOString();
    grant.expiresAsNumber = expiresMs;
  }
  return grant;
}

function grantIncludesFeature(org, featureId, nowMs) {
  const planKey = getActiveGrantPlanKey(org, nowMs);
  if (!planKey) {
    return false;
  }
  const plan = PLAN_CATALOG[planKey];
  return !!(plan && plan.features && plan.features[featureId]);
}

function getEffectivePropertyLimit(org, nowMs) {
  const account = getAccount(org);
  const stripeCount = Number(account.subscriptionCount);
  const stripePlan = PLAN_CATALOG[getPlanKey(org)];
  const stripeLimit = stripePlan && stripePlan.limits ? stripePlan.limits.properties : 0;
  let limit = 0;
  if (Number.isFinite(stripeCount) && stripeCount > 0) {
    limit = stripeCount;
  }
  if (stripeLimit > limit) {
    limit = stripeLimit;
  }
  const grantKey = getActiveGrantPlanKey(org, nowMs);
  if (grantKey && PLAN_CATALOG[grantKey] && PLAN_CATALOG[grantKey].limits) {
    const grantLimit = PLAN_CATALOG[grantKey].limits.properties;
    if (grantLimit > limit) {
      limit = grantLimit;
    }
  }
  return limit;
}

function isSubscriptionActive(org, nowMs) {
  const expiresMs = getAccountExpiresMs(getAccount(org));
  if (expiresMs === null) {
    return true;
  }
  return expiresMs + SUBSCRIPTION_GRACE_MS > nowMs;
}

function isLegacyPremium(org) {
  if (!org) {
    return false;
  }
  return org.subType === "Premium" || getAccount(org).premium === true;
}

function isProSubscriptionType(subscriptionType) {
  if (!subscriptionType) {
    return false;
  }
  if (
    subscriptionType === "Pro" ||
    subscriptionType === "Pro-10" ||
    subscriptionType === "PRO10" ||
    subscriptionType === "PRO10-90" ||
    subscriptionType === "sub_100_12m"
  ) {
    return true;
  }
  return normalizeTypeKey(subscriptionType).includes("pro10");
}

function getPlanKey(org) {
  if (!org) {
    return "trial";
  }
  const subscriptionType = getAccount(org).subscriptionType;
  if (org.subType === "Pro" && !isProSubscriptionType(subscriptionType)) {
    return "Pro";
  }
  if (subscriptionType && PLAN_CATALOG[subscriptionType]) {
    return subscriptionType;
  }
  if (subscriptionType && PLAN_ALIASES[subscriptionType]) {
    return PLAN_ALIASES[subscriptionType];
  }
  if (subscriptionType && normalizeTypeKey(subscriptionType).includes("pro10")) {
    return "PRO10-90";
  }
  if (isProSubscriptionType(subscriptionType) || org.subType === "Pro") {
    return "Pro";
  }
  if (subscriptionType === "GuestAsk") {
    return "GuestAsk";
  }
  if (!subscriptionType) {
    return "trial";
  }
  return "trial";
}

function getPlanFamily(org) {
  const plan = PLAN_CATALOG[getPlanKey(org)];
  return (plan && plan.family) || "free";
}

function orgHasProFamilyAccess(org, nowMs) {
  if (getPlanFamily(org) === "pro") {
    return true;
  }
  const grantKey = getActiveGrantPlanKey(org, nowMs);
  return !!(
    grantKey &&
    PLAN_CATALOG[grantKey] &&
    PLAN_CATALOG[grantKey].family === "pro"
  );
}

function isProViaCountOrPrice(org, nowMs) {
  const account = getAccount(org);
  if (!isSubscriptionActive(org, nowMs)) {
    return false;
  }
  if (Number(account.subscriptionCount) >= 100) {
    return true;
  }
  return (
    !!account.stripePriceId &&
    PRO_STRIPE_PRICE_IDS.indexOf(account.stripePriceId) !== -1
  );
}

function getPlanFeatures(org, nowMs) {
  const now = nowMs == null ? Date.now() : nowMs;
  const plan = PLAN_CATALOG[getPlanKey(org)] || PLAN_CATALOG.trial;
  const features = { ...plan.features };
  if (isLegacyPremium(org)) {
    features.guestAsk = true;
    features.listingImport = true;
  }
  if (isProViaCountOrPrice(org, now)) {
    features.guestAsk = true;
    features.listingImport = true;
  }
  return features;
}

function getPlanDefault(org, featureId, nowMs) {
  return !!getPlanFeatures(org, nowMs)[featureId];
}

function getOverrideState(org, featureId) {
  const value = getAccount(org).featureOverrides
    ? getAccount(org).featureOverrides[featureId]
    : undefined;
  if (value === true) {
    return "on";
  }
  if (value === false) {
    return "off";
  }
  return "default";
}

function applyOverrideState(overrides, featureId, state) {
  const next = { ...(overrides || {}) };
  if (state === "on") {
    next[featureId] = true;
  } else if (state === "off") {
    next[featureId] = false;
  } else {
    delete next[featureId];
  }
  return next;
}

function hasFeature(org, featureId, options) {
  const opts = options || {};
  const nowMs = opts.now == null ? Date.now() : opts.now;
  if (FEATURE_IDS.indexOf(featureId) === -1) {
    return false;
  }
  const override = getOverrideState(org, featureId);
  if (override === "off") {
    return false;
  }
  const grantHas = grantIncludesFeature(org, featureId, nowMs);
  const planHas = getPlanDefault(org, featureId, nowMs);
  if (override === "on") {
    if (opts.ignoreExpiry) {
      return true;
    }
    return isSubscriptionActive(org, nowMs) || isEntitlementGrantActive(org, nowMs);
  }
  if (opts.ignoreExpiry) {
    return planHas || grantHas;
  }
  if (grantHas) {
    return true;
  }
  if (!planHas) {
    return false;
  }
  return isSubscriptionActive(org, nowMs);
}

function legacyIsSubscribed(org, nowMs) {
  const expires = getAccount(org).expires;
  if (!expires) {
    return false;
  }
  return new Date(expires).getTime() > nowMs;
}

function legacyIsPro(org, nowMs) {
  if (!org || !org.account) {
    return false;
  }
  const account = getAccount(org);
  if (org.subType === "Pro" || isProSubscriptionType(account.subscriptionType)) {
    return true;
  }
  if (!legacyIsSubscribed(org, nowMs)) {
    return false;
  }
  if (Number(account.subscriptionCount) >= 100) {
    return true;
  }
  return (
    !!account.stripePriceId &&
    PRO_STRIPE_PRICE_IDS.indexOf(account.stripePriceId) !== -1
  );
}

function legacyIsGuestAsk(org, nowMs) {
  const account = getAccount(org);
  if (account.subscriptionType !== "GuestAsk") {
    return false;
  }
  if (!account.expires && typeof account.expiresAsNumber !== "number") {
    return true;
  }
  return legacyIsSubscribed(org, nowMs);
}

function legacyHasGuestQAAccess(org, nowMs) {
  return isLegacyPremium(org) || legacyIsPro(org, nowMs) || legacyIsGuestAsk(org, nowMs);
}

function legacyHasProBranding(org) {
  if (!org) {
    return false;
  }
  if (org.subType === "Pro") {
    return true;
  }
  return isProSubscriptionType(getAccount(org).subscriptionType);
}

function legacySubscriptionTypeHasPro(org) {
  const subscriptionType = getAccount(org).subscriptionType;
  return (
    typeof subscriptionType === "string" &&
    subscriptionType.toLowerCase().includes("pro")
  );
}

function legacyHasFeature(org, featureId, nowMs) {
  const now = nowMs == null ? Date.now() : nowMs;
  if (grantIncludesFeature(org, featureId, now)) {
    return true;
  }
  if (featureId === "guestAsk" || featureId === "listingImport") {
    return legacyHasGuestQAAccess(org, now);
  }
  if (featureId === "whiteLabel") {
    return legacyHasProBranding(org);
  }
  if (featureId === "customUrls" || featureId === "proPropertyFields") {
    return legacySubscriptionTypeHasPro(org);
  }
  return false;
}

function entitlementSource(org, featureId, nowMs) {
  const override = getOverrideState(org, featureId);
  if (override === "on") {
    return "On via override";
  }
  if (override === "off") {
    return "Off via override";
  }
  if (grantIncludesFeature(org, featureId, nowMs)) {
    const grantKey = getActiveGrantPlanKey(org, nowMs);
    const grant = getEntitlementGrant(org);
    const label = (PLAN_CATALOG[grantKey] && PLAN_CATALOG[grantKey].label) || grantKey;
    const expiresMs = getGrantExpiresMs(grant);
    if (expiresMs !== null) {
      return "On via grant (" + label + " until " + new Date(expiresMs).toISOString().slice(0, 10) + ")";
    }
    return "On via grant (" + label + ")";
  }
  if (!getPlanDefault(org, featureId, nowMs)) {
    return "Off via plan";
  }
  const planKey = getPlanKey(org);
  if (isLegacyPremium(org) && (featureId === "guestAsk" || featureId === "listingImport")) {
    const plan = PLAN_CATALOG[planKey];
    if (!plan.features[featureId]) {
      return "On via Premium";
    }
  }
  return "On via " + ((PLAN_CATALOG[planKey] && PLAN_CATALOG[planKey].label) || planKey);
}

function compareEntitlements(org, nowMs) {
  const now = nowMs == null ? Date.now() : nowMs;
  return FEATURE_IDS.map(function (featureId) {
    const effectiveNew = hasFeature(org, featureId, { now: now });
    const effectiveOld = legacyHasFeature(org, featureId, now);
    return {
      featureId: featureId,
      label: FEATURE_LABELS[featureId],
      planKey: getPlanKey(org),
      planDefault: getPlanDefault(org, featureId, now),
      override: getOverrideState(org, featureId),
      effectiveNew: effectiveNew,
      effectiveOld: effectiveOld,
      match: effectiveNew === effectiveOld,
      source: entitlementSource(org, featureId, now),
    };
  });
}

const entitlements = {
  FEATURE_IDS: FEATURE_IDS,
  FEATURE_LABELS: FEATURE_LABELS,
  PLAN_CATALOG: PLAN_CATALOG,
  PLAN_ALIASES: PLAN_ALIASES,
  GRANT_PLAN_KEYS: GRANT_PLAN_KEYS,
  GRANT_DURATION_OPTIONS: GRANT_DURATION_OPTIONS,
  SUBSCRIPTION_GRACE_MS: SUBSCRIPTION_GRACE_MS,
  PRO_STRIPE_PRICE_IDS: PRO_STRIPE_PRICE_IDS,
  getPlanKey: getPlanKey,
  getPlanFamily: getPlanFamily,
  orgHasProFamilyAccess: orgHasProFamilyAccess,
  getPlanFeatures: getPlanFeatures,
  getPlanDefault: getPlanDefault,
  getOverrideState: getOverrideState,
  applyOverrideState: applyOverrideState,
  hasFeature: hasFeature,
  legacyHasFeature: legacyHasFeature,
  compareEntitlements: compareEntitlements,
  isLegacyPremium: isLegacyPremium,
  isSubscriptionActive: isSubscriptionActive,
  entitlementSource: entitlementSource,
  getEntitlementGrant: getEntitlementGrant,
  getActiveGrantPlanKey: getActiveGrantPlanKey,
  isEntitlementGrantActive: isEntitlementGrantActive,
  buildEntitlementGrant: buildEntitlementGrant,
  grantIncludesFeature: grantIncludesFeature,
  getEffectivePropertyLimit: getEffectivePropertyLimit,
  getGrantExpiresMs: getGrantExpiresMs,
};

module.exports = entitlements;